import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useId, useRef } from 'react';
import { cn } from '../../lib/cn.js';
import { FOCUS_RING } from '../../lib/focus-ring.js';
/**
 * One choice between a few views of the same screen.
 *
 * Controlled, with no internal state. Which tab is showing is a screen's
 * business — it may come from a route, or be reset by something the screen
 * did — and a component that remembered it as well would be a second opinion
 * about the same thing.
 *
 * The keyboard behaviour is the one the ARIA tabs pattern specifies, and it
 * is the reason this is a component rather than two buttons: arrow keys move
 * between tabs, Home and End jump to the ends, and only the selected tab is
 * in the tab order, so Tab from the list lands in the panel rather than on
 * the next tab. Selection follows focus, which is the recommended behaviour
 * when switching panels is instant, as it is here — nothing loads.
 *
 * The panel is rendered by the caller and labelled from here. It carries no
 * `tabIndex`: the pattern asks for one only when a panel holds nothing
 * focusable, and every panel in this product holds a form.
 */
export function Tabs({ label, items, value, onValueChange, children, className, }) {
    const base = useId();
    const listRef = useRef(null);
    const selectedIndex = items.findIndex((item) => item.value === value);
    const tabId = (item) => `${base}-tab-${item.value}`;
    const panelId = (item) => `${base}-panel-${item.value}`;
    function onKeyDown(event) {
        const last = items.length - 1;
        const next = event.key === 'ArrowRight'
            ? (selectedIndex + 1) % items.length
            : event.key === 'ArrowLeft'
                ? (selectedIndex - 1 + items.length) % items.length
                : event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                        ? last
                        : null;
        if (next === null)
            return;
        const target = items[next];
        if (!target)
            return;
        event.preventDefault();
        onValueChange(target.value);
        // The buttons all exist already, so this focuses the right one before the
        // re-render that moves `tabindex` onto it.
        listRef.current?.querySelectorAll('[role="tab"]')[next]?.focus();
    }
    const selected = items[selectedIndex];
    return (_jsxs("div", { className: cn('flex flex-col gap-6', className), children: [_jsx("div", { ref: listRef, role: "tablist", "aria-label": label, onKeyDown: onKeyDown, className: "-m-1 flex gap-1 overflow-x-auto p-1", children: items.map((item) => {
                    const isSelected = item.value === value;
                    return (_jsx("button", { type: "button", role: "tab", id: tabId(item), "aria-selected": isSelected, "aria-controls": panelId(item), tabIndex: isSelected ? 0 : -1, onClick: () => onValueChange(item.value), className: cn('inline-flex min-h-touch-target-min shrink-0 items-center rounded-sm px-4 py-2', 'text-body-sm font-semibold transition-colors duration-fast', isSelected
                            ? cn('bg-primary-light text-primary-dark', 
                            // The tint is the selection, and forced colours drops
                            // it; the system highlight takes its place there.
                            'forced-colors:bg-[Highlight] forced-colors:text-[HighlightText] forced-colors:forced-color-adjust-none')
                            : 'text-text-secondary hover:bg-surface-secondary', FOCUS_RING), children: item.label }, item.value));
                }) }), selected ? (_jsx("div", { role: "tabpanel", id: panelId(selected), "aria-labelledby": tabId(selected), children: children })) : null] }));
}
//# sourceMappingURL=Tabs.js.map